import {
  flowRecipes,
  stepNeedsTool,
  type DraftStep,
  type ValidationIssue,
} from "@zworks/console-contracts";
import {
  AlertTriangle,
  Box,
  ChevronDown,
  ChevronRight,
  GitFork,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface CapabilityOption {
  id: string;
  label: string;
}

export function StepInspector({
  step,
  steps,
  disabled,
  issues,
  tools,
  skills,
  onChange,
  onRemove,
  onClose,
}: {
  step: DraftStep;
  steps: DraftStep[];
  disabled: boolean;
  issues: ValidationIssue[];
  tools: CapabilityOption[];
  skills: CapabilityOption[];
  onChange: (patch: Partial<DraftStep>) => void;
  onRemove: () => void;
  onClose?: () => void;
}) {
  const [podSectionOpen, setPodSectionOpen] = React.useState(true);
  const earlier = steps.filter((other) => other.position < step.position);
  const otherSteps = steps.filter((other) => other.id !== step.id);
  const stepIssues = issues.filter((issue) => issue.stepId === step.id);

  return (
    <form
      className="flex flex-col gap-3.5"
      onSubmit={(event) => event.preventDefault()}
      aria-label="Step"
    >
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold tracking-tight">Step {step.position}</h2>
          <Badge variant={step.gate ? "warning" : "secondary"}>
            {step.gate ? "Gate" : step.kind}
          </Badge>
        </div>
        {onClose ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:text-foreground"
            onClick={onClose}
            aria-label="Close inspector"
          >
            <X className="size-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>

      {/* Validation Issue Alerts for this step */}
      {stepIssues.length > 0 ? (
        <div className="space-y-1.5 rounded-lg border border-danger/40 bg-danger/5 p-2.5">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-danger">
            <AlertTriangle className="size-3.5" aria-hidden="true" />
            <span>Validation issues ({stepIssues.length})</span>
          </p>
          <ul className="space-y-1 text-xs text-danger/90">
            {stepIssues.map((issue) => (
              <li key={issue.rule} role="alert" className="leading-tight">
                • {issue.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <label className="block text-xs font-medium">
        Name
        <Input
          value={step.name}
          disabled={disabled}
          onChange={(event) => onChange({ name: event.target.value })}
          className="mt-1"
        />
      </label>

      <label className="block text-xs font-medium">
        Kind
        <Input
          value={step.kind}
          disabled={disabled}
          onChange={(event) => onChange({ kind: event.target.value })}
          className="mt-1"
        />
      </label>

      <label className="block text-xs font-medium">
        Tool capability
        <select
          aria-label="Tool capability"
          value={step.toolRef ?? ""}
          disabled={disabled}
          onChange={(event) => onChange({ toolRef: event.target.value || null })}
          className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm"
        >
          <option value="">No tool bound</option>
          {step.toolRef && !tools.some((tool) => tool.id === step.toolRef) ? (
            <option value={step.toolRef}>Unavailable tool</option>
          ) : null}
          {tools.map((tool) => (
            <option key={tool.id} value={tool.id}>
              {tool.label}
            </option>
          ))}
        </select>
      </label>
      {stepNeedsTool(step) && !tools.some((tool) => tool.id === step.toolRef) ? (
        <p className="text-xs font-medium text-danger">A tool is required for this step.</p>
      ) : null}

      <label className="block text-xs font-medium">
        Skill capability
        <select
          aria-label="Skill capability"
          value={step.skillRef ?? ""}
          disabled={disabled}
          onChange={(event) => onChange({ skillRef: event.target.value || null })}
          className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm"
        >
          <option value="">No skill bound</option>
          {step.skillRef && !skills.some((skill) => skill.id === step.skillRef) ? (
            <option value={step.skillRef}>Unavailable skill</option>
          ) : null}
          {skills.map((skill) => (
            <option key={skill.id} value={skill.id}>
              {skill.label}
            </option>
          ))}
        </select>
      </label>

      {!step.gate ? (
        <label className="block text-xs font-medium">
          Step spend cap (credits)
          <Input
            type="number"
            min={0}
            value={step.spendCap ?? ""}
            disabled={disabled}
            placeholder="e.g. 500"
            onChange={(event) => {
              const value = Number.parseInt(event.target.value, 10);
              onChange({ spendCap: Number.isFinite(value) && value >= 0 ? value : null });
            }}
            className="mt-1"
          />
        </label>
      ) : null}

      {!step.gate ? (
        <label className="block text-xs font-medium">
          Recipe
          <select
            value={step.recipe ?? ""}
            disabled={disabled}
            onChange={(event) => onChange({ recipe: event.target.value || undefined })}
            className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm"
          >
            <option value="">No recipe (the run cannot execute this step)</option>
            {(["auto", "agent", "runner"] as const).map((executor) => (
              <optgroup
                key={executor}
                label={
                  executor === "auto"
                    ? "Inside the engine"
                    : executor === "agent"
                      ? "Agent in a sandbox"
                      : "Flow runner"
                }
              >
                {flowRecipes
                  .filter((recipe) => recipe.executor === executor)
                  .map((recipe) => (
                    <option key={recipe.id} value={recipe.id}>
                      {recipe.label} ({recipe.id})
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
          {step.recipe ? (
            <span className="mt-1 block text-xs font-normal text-muted-foreground">
              {flowRecipes.find((recipe) => recipe.id === step.recipe)?.summary ??
                "This recipe does not exist."}
            </span>
          ) : null}
        </label>
      ) : null}

      {/* Pod Container Runtime Specification (Applicable for task/worker/deploy steps) */}
      {!step.gate ? (
        <div className="rounded-lg border border-border/80 bg-muted/20 overflow-hidden">
          <button
            type="button"
            onClick={() => setPodSectionOpen(!podSectionOpen)}
            className="flex items-center justify-between w-full p-2.5 text-xs font-semibold hover:bg-muted/40 transition-colors"
          >
            <div className="flex items-center gap-1.5">
              <Box className="size-3.5 text-accent-text" aria-hidden="true" />
              <span>Pod Container Spec</span>
            </div>
            {podSectionOpen ? (
              <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden="true" />
            ) : (
              <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden="true" />
            )}
          </button>

          {podSectionOpen ? (
            <div className="p-3 pt-0 space-y-3 text-xs border-t border-border/40">
              <label className="block text-xs font-medium pt-2">
                Container Image
                <Input
                  value={step.podConfig?.image ?? ""}
                  disabled={disabled}
                  placeholder="e.g. python:3.11-slim, node:22"
                  onChange={(e) =>
                    onChange({
                      podConfig: {
                        ...step.podConfig,
                        image: e.target.value || null,
                      },
                    })
                  }
                  className="mt-1"
                />
              </label>

              <div className="grid grid-cols-2 gap-2">
                <label className="block text-xs font-medium">
                  CPU Allocation
                  <select
                    className="mt-1 block w-full rounded-md border bg-background px-2.5 py-1.5 text-xs text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                    value={step.podConfig?.cpu ?? ""}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange({
                        podConfig: {
                          ...step.podConfig,
                          cpu: e.target.value || null,
                        },
                      })
                    }
                  >
                    <option value="">Default (250m)</option>
                    <option value="250m">250m (0.25 core)</option>
                    <option value="500m">500m (0.5 core)</option>
                    <option value="1">1 Core</option>
                    <option value="2">2 Cores</option>
                    <option value="4">4 Cores</option>
                  </select>
                </label>

                <label className="block text-xs font-medium">
                  RAM Allocation
                  <select
                    className="mt-1 block w-full rounded-md border bg-background px-2.5 py-1.5 text-xs text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
                    value={step.podConfig?.memory ?? ""}
                    disabled={disabled}
                    onChange={(e) =>
                      onChange({
                        podConfig: {
                          ...step.podConfig,
                          memory: e.target.value || null,
                        },
                      })
                    }
                  >
                    <option value="">Default (256Mi)</option>
                    <option value="256Mi">256 MiB</option>
                    <option value="512Mi">512 MiB</option>
                    <option value="1Gi">1 GiB</option>
                    <option value="2Gi">2 GiB</option>
                    <option value="4Gi">4 GiB</option>
                  </select>
                </label>
              </div>

              <label className="block text-xs font-medium">
                Parallel Pod Instances
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    min={1}
                    max={32}
                    value={step.podConfig?.parallel ?? 1}
                    disabled={disabled}
                    onChange={(e) => {
                      const count = Number.parseInt(e.target.value, 10);
                      onChange({
                        podConfig: {
                          ...step.podConfig,
                          parallel: Number.isFinite(count) && count > 0 ? count : 1,
                        },
                      });
                    }}
                    className="w-24"
                  />
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Zap className="size-3 text-accent-text" aria-hidden="true" />
                    concurrent worker pods
                  </span>
                </div>
              </label>

              <label className="block text-xs font-medium">
                Entrypoint Command
                <Input
                  value={step.podConfig?.command ?? ""}
                  disabled={disabled}
                  placeholder="e.g. python -u run.py"
                  onChange={(e) =>
                    onChange({
                      podConfig: {
                        ...step.podConfig,
                        command: e.target.value || null,
                      },
                    })
                  }
                  className="mt-1"
                />
              </label>

              {/* Parallel DAG Dependencies Selector */}
              {otherSteps.length > 0 ? (
                <div className="pt-1">
                  <label className="block text-xs font-medium mb-1 flex items-center gap-1">
                    <GitFork className="size-3 text-accent-text" aria-hidden="true" />
                    <span>Parallel Prerequisites (depends on)</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    Select upstream steps that must finish before this Pod starts:
                  </p>
                  <div className="space-y-1 max-h-28 overflow-y-auto rounded border bg-background/50 p-2">
                    {otherSteps.map((other) => {
                      const isChecked = (step.dependsOn ?? []).includes(other.id);
                      return (
                        <label
                          key={other.id}
                          className="flex items-center gap-2 text-xs hover:text-foreground cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={disabled}
                            onChange={(e) => {
                              const currentDeps = step.dependsOn ?? [];
                              const nextDeps = e.target.checked
                                ? [...currentDeps, other.id]
                                : currentDeps.filter((id) => id !== other.id);
                              onChange({ dependsOn: nextDeps });
                            }}
                            className="size-3.5 rounded border-border text-accent-text focus:ring-primary"
                          />
                          <span className="truncate">
                            {other.position}. {other.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <label className="flex items-center gap-2 text-xs font-medium pt-1">
        <input
          type="checkbox"
          checked={step.gate}
          disabled={disabled}
          onChange={(event) => onChange({ gate: event.target.checked })}
          className="size-4 rounded border-border text-accent-text focus:ring-primary"
        />
        This step is a gate
      </label>

      {step.gate ? (
        <div className="rounded-lg border border-border/80 bg-muted/20 p-3 space-y-3 mt-1">
          <label className="block text-xs font-medium">
            Who decides (comma separated)
            <Input
              value={step.deciders.join(", ")}
              disabled={disabled}
              placeholder="e.g. u-2, dev-lead"
              onChange={(event) =>
                onChange({
                  deciders: event.target.value
                    .split(",")
                    .map((value) => value.trim())
                    .filter(Boolean),
                })
              }
              className="mt-1"
            />
          </label>
          <p className="text-xs text-muted-foreground">
            The author of a change can never decide their own gate. A gate declined three times
            fails the run.
          </p>

          <label className="block text-xs font-medium">
            Expires after (minutes)
            <Input
              type="number"
              min={1}
              value={step.expiryMinutes ?? ""}
              disabled={disabled}
              placeholder="60"
              onChange={(event) => {
                const value = Number.parseInt(event.target.value, 10);
                onChange({ expiryMinutes: Number.isFinite(value) && value > 0 ? value : null });
              }}
              className="mt-1"
            />
          </label>

          <label className="block text-xs font-medium">
            On reject, go back to
            <select
              className="mt-1 block w-full rounded-md border bg-background px-2.5 py-1.5 text-xs text-foreground focus:ring-2 focus:ring-primary focus:outline-none"
              value={step.rejectRoute ?? ""}
              disabled={disabled}
              onChange={(event) => onChange({ rejectRoute: event.target.value || null })}
            >
              <option value="">No reject route</option>
              {earlier.map((other) => (
                <option key={other.id} value={other.id}>
                  {other.position}. {other.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {!disabled ? (
        <div className="pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRemove}
            className="w-full text-danger hover:bg-danger/10 hover:text-danger hover:border-danger/40"
          >
            <Trash2 className="size-3.5 mr-1.5" aria-hidden="true" />
            Remove step
          </Button>
        </div>
      ) : null}
    </form>
  );
}
